import { FC, useEffect, useRef, useState } from "react";
import cx from "classnames";
import Portal from "@app/shared/Portal";
import { QESButton } from "@app/shared/qes-standards";
import { ModalProps as ModalProps } from "./modalProps";
import { commonFocusableElements, focusFirstInteractiveElement } from "@app/shared/utility";
import { handleCssTransition } from "@app/shared/qes-standards/utility/handleCssTransition";

const Modal: FC<ModalProps> = (props: ModalProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [delayedAction, setDelayedAction] = useState<() => void>();
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableElRef = useRef<HTMLElement>(null);
  const lastFocusableElRef = useRef<HTMLElement>(null);
  const hasBeenVisible = useRef(false);

  const handleAction = (action: () => void) => {
    setDelayedAction(() => action);
  };

  useEffect(() => {
    if (isVisible) {
      hasBeenVisible.current = true;
    }
  }, [isVisible]);

  useEffect(() => {
    if (hasBeenVisible.current) {
      handleCssTransition(modalRef, "modal", !isVisible);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      const focusableEls = [...modalRef.current?.querySelectorAll(commonFocusableElements)] as HTMLElement[];
      firstFocusableElRef.current = focusableEls[0];
      lastFocusableElRef.current = focusableEls[focusableEls.length - 1];
      firstFocusableElRef.current.focus();
    } else {
      if (props.triggerElement?.current && hasBeenVisible.current) {
        props.triggerElement.current.focus();
      } else if (hasBeenVisible.current) {
        focusFirstInteractiveElement();
      }
    }
  }, [isVisible]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          if (isVisible) {
            handleAction(props.closeModal);
          }
          break;
        case "Tab":
          // don't let the user tab out of this modal
          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElRef.current) {
              lastFocusableElRef.current?.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastFocusableElRef.current) {
              firstFocusableElRef.current?.focus();
              e.preventDefault();
            }
          }
          break;
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isVisible]);

  useEffect(() => {
    if (props.isVisible) {
      setIsMounted(true);
      window.setTimeout(() => setIsVisible(true), 50);
    } else {
      window.setTimeout(() => setIsVisible(false), 50);
      setDelayedAction(undefined);
    }
  }, [props.isVisible]);

  useEffect(() => {
    if (props.isVisible && !!delayedAction) {
      window.setTimeout(() => {
        delayedAction();
      }, 300); // 300ms is the duration of the css animation
    } else if (!props.isVisible && !isVisible && !delayedAction) {
      setIsMounted(false);
    }
  }, [props.isVisible, isVisible, delayedAction]);
  
  return (
    isMounted && (
      <Portal
        hideBodyScroll={props.hideBodyScroll}
        container={props.container}
      >
        <div
          className={cx({
            "full-height": props.fullHeight,
            "new-modal": true,
          })}
          ref={modalRef}
        >
          <div
            className="new-modal__backdrop"
            data-testid="new-modal-backdrop"
            onClick={() => handleAction(props.closeModal)}
          />
          <div className={cx({
            "full-height": props.fullHeight,
            "new-modal__modal": true,
          })} onClick={(e) => e.stopPropagation()}>
            <>
              <h5 className="new-modal__title">{props.content.title}</h5>
              {/* I'd like this to be a p tag, but for the sake of valid nesting,
                  and not being able to prevent props.content.body from containing
                  other block level elements, a div will suffice
              */}
              <div className="new-modal__body">{props.content.body}</div>
              <div className="new-modal__actions">
                {props.content.cancelButtonText && (<QESButton
                  qesType="secondary-danger"
                  qesTestId="modal__cancel"
                  onClick={() => handleAction(props.closeModal)}
                >
                  {props.content.cancelButtonText}
                </QESButton>)}
                <QESButton
                  qesType={props.content.confirmButtonType || "primary"}
                  qesTestId="modal__continue"
                  onClick={() => handleAction(props.content.confirmButtonAction)}
                  disabled={props.content.confirmButtonDisable}
                >
                  {props.content.confirmButtonText}
                </QESButton>
              </div>
            </>
          </div>
        </div>
      </Portal>
    )
  );
}
export default Modal;